import SqOneTrust from '@square/one-trust';

// ///////////////////////////////////////////////
// OneTrust                                     //
// ///////////////////////////////////////////////

/**
 * Used only for making requests. Keys are 'C000[1-4]'. A value of "1" means
 * that cookie type is allowed.
 * strictlyNecessary: 'C0001'
 * performanceAndAnalytics: 'C0002'
 * functionality: 'C0003'
 * retargetingOrAdvertising: 'C0004'
 */
export type GroupConsentHash = Record<string, '0' | '1'>;

/**
 * A subset of the SqOneTrust data structure that's attached to the window in Dashboard
 * as window.SqOneTrust.
 *
 * Doc with definitions of cookie types:
 * https://docs.google.com/document/d/1Jl-bH9g8lrDFtHDzWymhzwSMAgkeQDQyA5alVP5xEng/edit#heading=h.49ggydf7ilvt
 */
export type MessagesSqOneTrust = {
  // Will only be true if a person has blocked all cookies
  strictlyNecessaryCookiesBlocked: boolean;
  // Will be true if a person has blocked all cookies or specifically blocked
  // functionality cookies (e.g. cookies to indicate whether educational content
  // has already been displayed)
  functionalityCookiesBlocked: boolean;
  // Will be true if a person has blocked all cookies or specifically blocked
  // analytics cookies (e.g. CDP cookies)
  performanceAndAnalyticsCookiesBlocked: boolean;
  // Will be true if a person has blocked all cookies or specifically blocked
  // retargeting or advertising technology cookies (i.e. Google Analytics)
  retargetingOrAdvertisingCookiesBlocked: boolean;
  // Used only for making requests. Keys are 'C000[1-4]'. A value of "1" means
  // that cookie type is allowed.
  groupConsentHash: GroupConsentHash;
};

type OneTrustWindow = Window & {
  SqOneTrust: SqOneTrust;
  OneTrust: {
    SetAlertBoxClosed: () => null;
    ToggleInfoDisplay: () => null;
    GetDomainData: () => {
      ConsentModel: {
        Name: string;
      };
    };
  };
};

export const onetrustWindow = window as unknown as OneTrustWindow;

export enum OneTrustConsent {
  STRICTLY_NECESSARY = 'C0001',
  PERFORMANCE = 'C0002',
  FUNCTIONAL = 'C0003',
  TARGETING = 'C0004',
}

export class EnvoyConsentHeader {
  'X-Block-Cookies'?: string;
  'X-Allow-Cookies'?: string;
}

export const X_ALLOW_COOKIES = 'X-Allow-Cookies';
export const X_BLOCK_COOKIES = 'X-Block-Cookies';
export const X_BLOCK_COOKIES_DEFAULT_VALUE = 'true';
export const X_CSRF_Token = 'X-CSRF-Token';
export const POST_NEW_ONE_TRUST_INSTANCE =
  'dashboardSqOneTrustInstanceChangedEvent';
export const ONE_TRUST_ALERT_COOKIE = 'OptanonAlertBoxClosed';

function getOneTrustActiveGroups() {
  return window.OnetrustActiveGroups;
}

function getOneTrustConsentModel() {
  return onetrustWindow.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
}

export function hasConsentBeenSet(): boolean {
  // `getOneTrustActiveGroups()` will only be undefined if the OneTrust script has not run.
  const noConsentFound = !getOneTrustActiveGroups();
  // If the merchant has anything other than strictly necessary cookies, they have either selected consent or been auto-consented (US/CA).
  const hasAutoConsented = /C000[^1]/.test(getOneTrustActiveGroups());
  // If the merchant is in an opt-out region, they will be auto-consented
  const isInOptOutRegion = getOneTrustConsentModel() === 'opt-out';
  // If neither of those are true, and there is an `OptanonAlertBoxClosed` cookie, the merchant has consented through the modal.
  const hasManuallyConsented = document.cookie.includes(ONE_TRUST_ALERT_COOKIE);

  return Boolean(
    noConsentFound ||
      hasAutoConsented ||
      isInOptOutRegion ||
      hasManuallyConsented,
  );
}

export function hasConsent(consentLevel: OneTrustConsent): boolean {
  if (!consentLevel) {
    throw new Error(
      'Consent level must be provided for OneTrust. Consent levels are available in `@square/dashboard-utilities/utils/one-trust/one-trust-compliance`.',
    );
  }

  // Default to true if NODE_ENV is testing
  return (
    !(
      process.env.NODE_ENV === 'production' ||
      process.env.NODE_ENV === 'development'
    ) || onetrustWindow.SqOneTrust?.groupConsentHash[consentLevel] === '1'
  );
}

export function generateEnvoyConsentHeader(): EnvoyConsentHeader {
  if (
    !(
      process.env.NODE_ENV === 'production' ||
      process.env.NODE_ENV === 'development'
    )
  ) {
    // NODE_ENV is testing
    return {};
  }

  const envoyConsentDefaultHeader = {
    [X_BLOCK_COOKIES]: X_BLOCK_COOKIES_DEFAULT_VALUE,
  };

  // If the onetrust script ran, oneTrustActivityGroups will always be populated with C0001
  const activeGroups = getOneTrustActiveGroups();
  if (hasConsentBeenSet() && activeGroups) {
    // X-Block-Cookies is always included to inform server that we need to filter cookies based
    // on X-Allow-Cookies categories.
    return {
      ...envoyConsentDefaultHeader,
      [X_ALLOW_COOKIES]: activeGroups,
    };
  }

  return envoyConsentDefaultHeader;
}
